import { ClicksBusinessItem, ClicksBusinessSort } from "../api/analyticsAdmin";

// minOpens only meaningfully affects rate-based sorts, where a tiny-sample
// business would otherwise dominate the ranking.
export const sortUsesMinOpens = (sort: ClicksBusinessSort): boolean =>
  sort === "OutboundRate" || sort === "ConversionRate";

// The lookup filters the already-fetched funnel rows, so it is instant and
// cannot fail — the request now covers every business active in the window,
// which is what makes "not found" mean "no activity" rather than "outside the
// top 50". Case-insensitive substring; the id is matched too, because rows
// without a display name render it in the table.
export const filterBusinesses = (
  rows: ClicksBusinessItem[],
  query: string
): ClicksBusinessItem[] => {
  const needle = query.trim().toLowerCase();
  if (needle === "") return rows;
  return rows.filter((row) =>
    (row.displayName ?? row.businessId).toLowerCase().includes(needle)
  );
};

// Suggestions for the lookup's dropdown, from the rows already on the client.
// Prefix matches come before mid-word ones — someone typing "ti" is far more
// likely after "Tivoli" than "Multigavekort" — and each group orders by opens,
// so the businesses a reader most plausibly means surface first. Names repeat
// across business ids (two Zalando rows exist in production), so suggestions
// dedupe on the visible label; picking one filters the table, where every row
// behind that name shows.
export const suggestBusinesses = (
  rows: ClicksBusinessItem[],
  query: string,
  limit: number
): string[] => {
  const needle = query.trim().toLowerCase();
  if (needle === "") return [];

  const seen = new Set<string>();
  const prefix: { label: string; opens: number }[] = [];
  const infix: { label: string; opens: number }[] = [];

  for (const row of rows) {
    const label = row.displayName ?? row.businessId;
    const lower = label.toLowerCase();
    if (!lower.includes(needle) || seen.has(lower)) continue;
    seen.add(lower);
    (lower.startsWith(needle) ? prefix : infix).push({ label, opens: row.opens });
  }

  const byOpens = (a: { opens: number }, b: { opens: number }) => b.opens - a.opens;
  return [...prefix.sort(byOpens), ...infix.sort(byOpens)]
    .slice(0, limit)
    .map((s) => s.label);
};

// A rate off an empty denominator is not a rate. SQL got this from NULLIF, which
// put those rows last; here they are marked and pushed the same way, rather than
// being read as a rate of zero and ranked among the genuinely poor performers.
const rateOrNull = (numerator: number, denominator: number): number | null =>
  denominator > 0 ? numerator / denominator : null;

const byRateThenVolume = (
  a: ClicksBusinessItem,
  b: ClicksBusinessItem,
  rate: (row: ClicksBusinessItem) => number | null
): number => {
  const left = rate(a);
  const right = rate(b);
  if (left === null && right === null) return b.opens - a.opens;
  if (left === null) return 1;
  if (right === null) return -1;
  return right - left || b.opens - a.opens;
};

/**
 * Orders the businesses the server returned. This used to be a `sort` parameter
 * on the request, so every change of the dropdown re-ran a query that chains
 * materialised CTEs over posthog.events — and on production data outlived the
 * gateway timeout, so switching sort showed an error rather than a reordering.
 *
 * Every field these orderings need is already in the payload, so the server now
 * answers one cacheable question and the ordering happens here. The comparators
 * mirror BusinessSortExpr in AnalyticsClicksReadRepository; keep them in step.
 */
export const rankBusinesses = (
  rows: ClicksBusinessItem[],
  sort: ClicksBusinessSort,
  minOpens: number
): ClicksBusinessItem[] => {
  const eligible = sortUsesMinOpens(sort)
    ? rows.filter((row) => row.opens >= minOpens)
    : rows;

  const ordered = [...eligible];

  switch (sort) {
    case "OutboundRate":
      return ordered.sort((a, b) =>
        byRateThenVolume(a, b, (row) => rateOrNull(row.opensWithOutbound, row.opens))
      );
    case "ConversionRate":
      return ordered.sort((a, b) =>
        byRateThenVolume(a, b, (row) => rateOrNull(row.convertedPairs, row.openedPairs))
      );
    case "ZeroConversion":
      return ordered.sort(
        (a, b) => a.convertedPairs - b.convertedPairs || b.opens - a.opens
      );
    default:
      return ordered.sort((a, b) => b.opens - a.opens);
  }
};
